import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createContext,
  type CSSProperties,
  lazy,
  Suspense,
  useEffect,
  useState,
} from "react";
import { Helmet } from "react-helmet";
import {
  type LoaderFunction,
  Outlet,
  ScrollRestoration,
  useLoaderData,
  useRevalidator,
} from "react-router-dom";

import { OpenMobileAppBanner } from "@/molecules/open-mobile-app-banner/OpenMobileAppBanner";
import {
  getAppsFlyerParams,
  getReferralCodeFromUrl,
  LINK_TYPES,
  openLinkToMobileApp,
} from "@/services/DeepLinkService";
import { get } from "api";
import AppUrlListener from "appUrlListener";
import { CartContext, useCartState } from "contexts/CartContext";
import { AnalyticsPageTracker } from "modules/analytics/AnalyticsPage/AnalyticsPage";
import { AppVersionCheck } from "modules/appVersion";
import Favicon from "pages/Favicon";
import { hexToRgb } from "toolbox/Colors";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 5,
    },
  },
});

const ReactQueryDevtools = lazy(() =>
  import("@tanstack/react-query-devtools/build/lib/index.prod.js").then(
    ({ ReactQueryDevtools }) => ({ default: ReactQueryDevtools })
  )
);

/**
 * Render the React Query Devtools in development and (on demand) in production
 */
const ReactQueryDevTools = () => {
  const [showTools, setShowTools] = useState(
    // Show devtools only in development on the web by default
    import.meta.env.DEV && !Capacitor.isNativePlatform()
  );

  useEffect(() => {
    // @ts-expect-error This function isn't called from the app, it's just for
    // debugging purposes in production
    window.toggleReactQueryDevTools = () => setShowTools((prev) => !prev);
  }, []);

  if (!showTools) return null;

  return (
    <Suspense fallback={null}>
      <ReactQueryDevtools />
    </Suspense>
  );
};

const transformThemeToCss = (theme: {
  brandColor?: string | null;
  brandTint?: string | null;
}) => {
  return {
    "--repeatmd-brand-color": hexToRgb(theme.brandColor),
    "--repeatmd-brand-tint": hexToRgb(theme.brandTint ?? theme.brandColor, 0.1),
  } as CSSProperties;
};

const load = async () => {
  const platform = await queryClient.ensureQueryData({
    queryKey: ["/platform/current"],
    queryFn: () => get("/platform/current").catch(() => null),
  });

  const platformId = platform?.id;
  const mobileAppDiscovery = platform?.platformSettings?.mobileAppDiscovery;

  if (Capacitor.isNativePlatform()) {
    if (platformId) {
      const theme = await queryClient.ensureQueryData(
        ["/multi-platform/platform/{platformId}/theme", { platformId }],
        () => get("/multi-platform/platform/{platformId}/theme", { platformId })
      );

      return {
        theme: transformThemeToCss(theme),
        platformId,
      };
    }
  }

  const theme = await queryClient.fetchQuery(["/platform/theme"], () =>
    get("/platform/theme")
  );

  const bannerParams = await getAppsFlyerParams(
    location.href,
    LINK_TYPES.BANNER,
    platformId
  );

  return {
    theme: transformThemeToCss(theme),
    platformId,
    mobileAppDiscovery,
    bannerParams,
  };
};

export const rootLoader: LoaderFunction = () => {
  return load();
};

type LoaderData = Awaited<ReturnType<typeof load>>;

export const ThemeContext = createContext({
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  resetTheme: () => {},
});

const Root = () => {
  const { theme, platformId, mobileAppDiscovery, bannerParams } =
    useLoaderData() as LoaderData;

  const showSmartBanner =
    !Capacitor.isNativePlatform() &&
    (mobileAppDiscovery || platformId === undefined);

  const cartState = useCartState();
  const revalidator = useRevalidator();
  const referralCode = getReferralCodeFromUrl(location.href);

  useEffect(() => {
    SplashScreen.hide();
  }, []);

  const resetTheme = () => {
    revalidator.revalidate();
  };

  const renderAppsFlyerSmartBanner = () => {
    return (
      <Helmet>
        <script>
          {`
          !function(t,e,n,s,a,c,i,o,p){t.AppsFlyerSdkObject=a,t.AF=t.AF||function(){(t.AF.q=t.AF.q||[]).push([Date.now()].concat(Array.prototype.slice.call(arguments)))},t.AF.id=t.AF.id||i,t.AF.plugins={},o=e.createElement(n),p=e.getElementsByTagName(n)[0],o.async=1,o.src="https://websdk.appsflyer.com?"+(c.length>0?"st="+c.split(",").sort().join(",")+"&":"")+(i.length>0?"af_id="+i:""),p.parentNode.insertBefore(o,p)}(window,document,"script",0,"AF","banners",{banners: {key: "0c9a84e8-2164-44d0-acfb-9d75834d8547"}});
          AF('banners', 'showBanner', ${JSON.stringify({
            additionalParams: bannerParams,
          })})
          `}
        </script>
      </Helmet>
    );
  };

  return (
    <QueryClientProvider client={queryClient}>
      <AnalyticsPageTracker />
      <AppVersionCheck />
      <AppUrlListener resetTheme={resetTheme} />
      <>
        {showSmartBanner && renderAppsFlyerSmartBanner()}
        {showSmartBanner && (
          <OpenMobileAppBanner
            onClick={() => openLinkToMobileApp(platformId, referralCode)}
          />
        )}
        <Favicon />
        <CartContext.Provider value={cartState}>
          <div style={theme}>
            <ThemeContext.Provider value={{ resetTheme }}>
              <Outlet />
            </ThemeContext.Provider>
          </div>
          <ScrollRestoration />
        </CartContext.Provider>
      </>
      <ReactQueryDevTools />
    </QueryClientProvider>
  );
};

export default Root;
